import { postWorkflowNotificationResumeTurn, resumeWorkflowNotificationInChat } from '../../api/workflowNotifications';
import { extractMrRouterResponse } from './mrHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractConstrainedNativeWorkflowEntry } from './constrainedWorkflowProjection';
import { confirmConstrainedGpcWorkflowEntry, postConstrainedGpcWorkflowTurn, postMrChatQuestionsNotification, postMrChatQuestionsShareAndNotify, postMrChatSectionCHandoff, refreshBoundMrChat, selectConstrainedWorkflowTypedEntryChoice } from './api';
import { useCallback } from 'react';
import type { GpcChatWorkflowStageKey } from '../GpcChat/types';
import { extractGpcRouterResponse } from './gpcHandoff';
import type { GpcChatSessionView, GpcChatTurnInput } from '../GpcChat/types';
import type { MrChatSessionView, MrChatTurnInput } from '../MrChat/types';
import type { ConstructionSowChatSessionView, ConstructionSowChatTurnInput, ConstructionSowArtifactRole } from '../ConstructionSowChat/types';
import type { ServicesPwsArtifactRole, ServicesPwsChatSessionView, ServicesPwsChatTurnInput } from '../ServicesPwsChat/types';
import { postV2Turn, postLazyCreateV2Turn, postMrChatTurn, postConstructionSowChatTurn, uploadConstructionSowRequirementDocument, extractConstructionSowRequirementDocument, removeConstructionSowRequirementDocument, uploadConstructionSowSupportDocument, replaceConstructionSowSupportDocument, removeConstructionSowSupportDocument, openGeneratedConstructionSow as apiOpenGeneratedConstructionSow, postServicesPwsChatTurn, uploadServicesPwsRequirementDocument, extractServicesPwsRequirementDocument, removeServicesPwsRequirementDocument, uploadServicesPwsSupportDocument, replaceServicesPwsSupportDocument, removeServicesPwsSupportDocument, openGeneratedServicesPws as apiOpenGeneratedServicesPws, uploadMrVendorQuotes } from './api';
import type { HomeChatHandoffContext } from './types';
import type { Turn, ClarificationEnvelope, TopicClarificationEnvelope } from '../FarChat/types';
import { isClarificationEnvelope, isTopicClarificationEnvelope } from '../FarChat/types';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { HOME_NAVIGATION_CHECKPOINT } from './homeChatChoiceUtils';
import { PENDING_ID, makeRequestId } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatHydration } from './useHomeChatHydration';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';


type Inputs = Pick<HomeChatPanelState, 'mrNotificationBoundResumeId' | 'mrObserverNotificationId' | 'setMrChatView' | 'setMrNotificationBoundResumeId' | 'setMrObserverNotificationId' | 'setWorkflowOnlyChoice' | 'workflowOnlyChoice'> &
Pick<Props, 'onStartWorkflowType' | 'workflowOnly'> &
Pick<HomeChatPanelState,
  | 'pendingGpcRouter'
  | 'inFlight'
  | 'setOptimisticGpcOpening'
  | 'setInFlight'
  | 'setPendingRequestIdSynced'
  | 'activeSessionId'
  | 'deliverResponseLlmResultEvents'
  | 'setPendingGpcRouter'
  | 'setPendingClarification'
  | 'maybePlayClarificationSound'
  | 'setPendingTopicClarification'
  | 'setActiveSessionId'
  | 'setTurns'
  | 'inputContainerRef'
  | 'gpcChatView'
  | 'mrChatView'
  | 'setMrTurnInFlight'
  | 'activeSessionIdRef'
  | 'interactionRevisionRef'
  | 'constructionSowChatView'
  | 'servicesPwsChatView'
> &
Pick<HomeChatHydration,
  | 'isStaleResolve'
  | 'applyGpcChatView'
  | 'notify'
  | 'clearPendingIfOwned'
  | 'applyMrChatView'
  | 'applyConstructionSowChatView'
  | 'applyServicesPwsChatView'
> &
Pick<HomeChatSessionReconciliation,
  | 'refreshSessions'
> &
Pick<Props, 'onBeforeWorkflowNavigation' | 'onOpenConstructionSowWorkflow' | 'onOpenGeneratedServicesPwsWorkflow' | 'onOpenMrWorkflow' | 'onOpenWorkflow'> &
Pick<Props, 'displayMode'>;

export function useHomeChatWorkflowHandoffs({
  pendingGpcRouter,
  inFlight,
  setOptimisticGpcOpening,
  setInFlight,
  setPendingRequestIdSynced,
  activeSessionId,
  isStaleResolve,
  deliverResponseLlmResultEvents,
  applyGpcChatView,
  refreshSessions,
  setPendingGpcRouter,
  setPendingClarification,
  maybePlayClarificationSound,
  setPendingTopicClarification,
  notify,
  setActiveSessionId,
  setTurns,
  clearPendingIfOwned,
  inputContainerRef,
  gpcChatView,
  mrChatView,
  setMrTurnInFlight,
  applyMrChatView,
  activeSessionIdRef,
  interactionRevisionRef,
  displayMode,
  onBeforeWorkflowNavigation,
  applyConstructionSowChatView,
  constructionSowChatView,
  onOpenConstructionSowWorkflow,
  applyServicesPwsChatView,
  servicesPwsChatView,
  onOpenGeneratedServicesPwsWorkflow,
  onOpenMrWorkflow,
  onOpenWorkflow,
  mrNotificationBoundResumeId,
  mrObserverNotificationId,
  setMrChatView,
  setMrNotificationBoundResumeId,
  setMrObserverNotificationId,
  setWorkflowOnlyChoice,
  workflowOnlyChoice,
  onStartWorkflowType,
  workflowOnly,
}: Inputs) {

  const submitGpcConfirmation = useCallback(async (confirmation: 'confirmed' | 'far_qa' | 'revise') => {
    const pending = pendingGpcRouter;
    if (!pending || (pending.kind !== 'start_confirmation' && pending.kind !== 'ambiguous_choice') || inFlight) return;
    const requestId = makeRequestId();
    let focusComposerAfterRevision = false;
    if (confirmation === 'confirmed') setOptimisticGpcOpening(pending.originalMessage);
    setInFlight(true);
    setPendingRequestIdSynced(requestId);
    try {
      const raw = workflowOnly
        ? await confirmConstrainedGpcWorkflowEntry(
          activeSessionId || '', pending.handoffId || '', confirmation === 'far_qa' ? 'revise' : confirmation, requestId,
        )
        : activeSessionId
        ? await postV2Turn(activeSessionId, pending.originalMessage, requestId, undefined, undefined, undefined, {
          gpcConfirmation: confirmation,
          ...(pending.handoffId ? { gpcHandoffId: pending.handoffId } : {}),
          })
        : await postLazyCreateV2Turn(pending.originalMessage, requestId, undefined, undefined, undefined, {
          gpcConfirmation: confirmation,
          ...(pending.handoffId ? { gpcHandoffId: pending.handoffId } : {}),
          });
      // A navigation or newer request invalidates this router response. In
      // particular, it must not surface a clarification card (or its chime)
      // for a superseded branch.
      if (isStaleResolve(requestId)) return;
      deliverResponseLlmResultEvents(raw);
      const response = extractGpcRouterResponse(raw);
      if (response?.kind === 'chat') {
        applyGpcChatView(response.view);
        await refreshSessions();
        setOptimisticGpcOpening(null);
        setPendingGpcRouter(null);
      } else if (response?.kind === 'start_confirmation' || response?.kind === 'ambiguous_choice' || response?.kind === 'resume_selection') {
        setPendingGpcRouter(response);
      } else if (confirmation === 'far_qa') {
        // The router sent the original message back through the ordinary Home
        // Chat Q&A branch. Render that normal response in the existing thread
        // rather than treating it as a failed GPC continuation.
        setPendingGpcRouter(null);
        if (isClarificationEnvelope(raw as Turn | ClarificationEnvelope | TopicClarificationEnvelope)) {
          const envelope = raw as ClarificationEnvelope;
          setPendingClarification({
            ...envelope,
            ...(activeSessionId ? { session_id: activeSessionId } : {}),
          });
          maybePlayClarificationSound(envelope, activeSessionId, requestId);
        } else if (isTopicClarificationEnvelope(raw as Turn | ClarificationEnvelope | TopicClarificationEnvelope)) {
          const envelope = raw as TopicClarificationEnvelope;
          setPendingTopicClarification({
            ...envelope,
            ...(activeSessionId ? { session_id: activeSessionId } : {}),
          });
          maybePlayClarificationSound(envelope, activeSessionId, requestId);
        } else {
          const lazyResponse = raw as { session_id?: string; turn?: Turn };
          const turn = lazyResponse.turn || (raw as Turn);
          if (!turn || typeof turn !== 'object') {
            notify('error', 'Unable to continue the FAR/RFO question. Please try again.');
            return;
          }
          if (!activeSessionId && lazyResponse.session_id) setActiveSessionId(lazyResponse.session_id);
          setTurns((previous) => [...previous, turn]);
        }
      } else if (confirmation === 'revise' && response?.kind === 'revision') {
        setOptimisticGpcOpening(null);
        setPendingGpcRouter(null);
        if (workflowOnly) {
          window.dispatchEvent(new CustomEvent('home-chat:focus', {
            detail: { message: pending.originalMessage },
          }));
        } else {
          focusComposerAfterRevision = true;
        }
      } else {
        if (confirmation === 'confirmed') setOptimisticGpcOpening(null);
        setPendingGpcRouter(null);
        notify('error', 'Unable to continue the GPC purchase. Please try again.');
      }
    } catch {
      if (confirmation === 'confirmed') setOptimisticGpcOpening(null);
      notify('error', 'Unable to continue the GPC purchase. Please try again.');
    } finally {
      setInFlight(false);
      clearPendingIfOwned(requestId);
      if (focusComposerAfterRevision) {
        window.setTimeout(() => {
          const input = inputContainerRef.current?.querySelector('textarea');
          if (input) (input as HTMLTextAreaElement).focus();
        }, 0);
      }
    }
  }, [activeSessionId, applyGpcChatView, clearPendingIfOwned, deliverResponseLlmResultEvents, inFlight, isStaleResolve, maybePlayClarificationSound, notify, pendingGpcRouter, refreshSessions, setPendingRequestIdSynced, workflowOnly]);

  const submitWorkflowOnlyChoice = useCallback(async (selectionKey: string) => {
    const card = workflowOnlyChoice;
    const cardId = typeof card?.card_id === 'string' ? card.card_id : null;
    if (!workflowOnly || !activeSessionId || !cardId || !selectionKey || inFlight) {
      notify('error', 'This workflow choice is no longer available.');
      return;
    }
    const requestId = makeRequestId();
    const homeSessionId = activeSessionId;
    const navigationRevision = interactionRevisionRef.current;
    const isCurrent = () => activeSessionIdRef.current === homeSessionId
      && interactionRevisionRef.current === navigationRevision
      && !isStaleResolve(requestId);
    setInFlight(true);
    setPendingRequestIdSynced(requestId);
    try {
      const raw = await selectConstrainedWorkflowTypedEntryChoice(
        homeSessionId, cardId, selectionKey, requestId,
      );
      if (!isCurrent()) return;
      const mr = extractMrRouterResponse(raw);
      const gpc = extractGpcRouterResponse(raw);
      const constructionSow = extractConstructionSowRouterResponse(raw);
      const nativeEntry = extractConstrainedNativeWorkflowEntry(raw);
      if (mr) {
        setMrChatView(mr);
        setWorkflowOnlyChoice(null);
      } else if (constructionSow) {
        applyConstructionSowChatView(constructionSow);
        setWorkflowOnlyChoice(null);
      } else if (nativeEntry && onStartWorkflowType) {
        // FlowSelector redeems this owner/session-bound receipt.  The panel
        // never receives a flow id or trusts the displayed button label.
        onStartWorkflowType(nativeEntry.workflowType, {
          homeChatSessionId: homeSessionId,
          workflowHandoffReceipt: nativeEntry.workflowHandoffReceipt,
          ...(nativeEntry.workflowEntryMode ? { workflowEntryMode: nativeEntry.workflowEntryMode } : {}),
        });
        setWorkflowOnlyChoice(null);
      } else if (gpc && (gpc.kind === 'start_confirmation' || gpc.kind === 'ambiguous_choice' || gpc.kind === 'resume_selection')) {
        setPendingGpcRouter(gpc);
        setWorkflowOnlyChoice(null);
      } else {
        throw new Error('Workflow choice is unavailable');
      }
      await refreshSessions();
    } catch (error: any) {
      if (isCurrent()) notify('error', error?.message || 'This workflow choice is no longer available.');
    } finally {
      if (isCurrent()) setInFlight(false);
      clearPendingIfOwned(requestId);
    }
  }, [activeSessionId, activeSessionIdRef, applyConstructionSowChatView, clearPendingIfOwned, inFlight, interactionRevisionRef, isStaleResolve, notify, onStartWorkflowType, refreshSessions, setPendingRequestIdSynced, workflowOnly, workflowOnlyChoice]);

  const submitBoundGpcTurn = useCallback(async (input: GpcChatTurnInput): Promise<GpcChatSessionView> => {
    const gpcSessionId = gpcChatView?.session_id;
    if (!gpcSessionId) throw new Error('GPC session is unavailable.');
    const gpc = {
      gpcChatSessionId: gpcSessionId,
      ...(input.confirmation ? { gpcConfirmation: input.confirmation } : {}),
      ...(input.input ? { gpcInput: input.input } : {}),
    };
    const raw = workflowOnly
      ? await postConstrainedGpcWorkflowTurn(activeSessionId || '', {
        client_request_id: input.client_request_id,
        ...(input.message ? { message: input.message } : {}),
        ...(input.input ? { input: input.input as Record<string, unknown> } : {}),
      })
      : activeSessionId
      ? await postV2Turn(activeSessionId, input.message || '', input.client_request_id, undefined, undefined, undefined, gpc)
      : await postLazyCreateV2Turn(input.message || '', input.client_request_id, undefined, undefined, undefined, gpc);
    deliverResponseLlmResultEvents(raw);
    const response = extractGpcRouterResponse(raw);
    if (response?.kind !== 'chat') throw new Error('Unable to update the GPC purchase. Please try again.');
    applyGpcChatView(response.view);
    return response.view;
  }, [activeSessionId, applyGpcChatView, deliverResponseLlmResultEvents, gpcChatView, workflowOnly]);

  const submitBoundMrTurn = useCallback(async (input: MrChatTurnInput): Promise<MrChatSessionView> => {
    const mrSessionId = mrChatView?.session_id;
    if (!activeSessionId || activeSessionId === PENDING_ID || !mrSessionId) {
      throw new Error('Market Research session is unavailable.');
    }
    const homeSessionId = activeSessionId;
    setMrTurnInFlight({
      homeSessionId,
      requestId: input.client_request_id,
    });
    try {
      const view = await postMrChatTurn(homeSessionId, mrSessionId, input);
      deliverResponseLlmResultEvents(view);
      applyMrChatView(view);
      return view;
    } finally {
      setMrTurnInFlight((current) => (
        current?.homeSessionId === homeSessionId
          && current.requestId === input.client_request_id
          ? null
          : current
      ));
    }
  }, [activeSessionId, applyMrChatView, deliverResponseLlmResultEvents, mrChatView]);

  const submitNotificationBoundMrTurn = useCallback(async (
    input: MrChatTurnInput,
  ): Promise<MrChatSessionView> => {
    const homeSessionId = activeSessionIdRef.current || activeSessionId;
    const navigationRevision = interactionRevisionRef.current;
    const isCurrent = () => activeSessionIdRef.current === homeSessionId
      && interactionRevisionRef.current === navigationRevision;
    // An interrupted observer has no general Home Chat turn authority (in
    // particular for CE). Its only writable action is the opaque Resume
    // notification route; after a successful promotion ordinary controller
    // polling resumes and this handle is intentionally discarded.
    if (input.action === 'resume' && mrObserverNotificationId) {
      const resumed = await resumeWorkflowNotificationInChat(
        mrObserverNotificationId, input.client_request_id,
      );
      if (isCurrent()) {
        setMrObserverNotificationId(null);
        setMrNotificationBoundResumeId(resumed.notificationBoundResumeId || mrObserverNotificationId);
        applyMrChatView(resumed.mrChat);
      }
      return resumed.mrChat;
    }
    if (input.action === 'resume' && mrNotificationBoundResumeId) {
      const resumed = await resumeWorkflowNotificationInChat(
        mrNotificationBoundResumeId, input.client_request_id,
      );
      if (isCurrent()) applyMrChatView(resumed.mrChat);
      return resumed.mrChat;
    }
    if (mrNotificationBoundResumeId) {
      const view = await postWorkflowNotificationResumeTurn(mrNotificationBoundResumeId, {
        client_request_id: input.client_request_id,
        ...(input.input ? { input: input.input as Record<string, unknown> } : {}),
      });
      if (isCurrent()) applyMrChatView(view);
      return view;
    }
    return submitBoundMrTurn(input);
  }, [activeSessionId, applyMrChatView, mrNotificationBoundResumeId, mrObserverNotificationId, submitBoundMrTurn]);

  // A typed MR execution conflict is recovered by rehydrating the same
  // owner-bound controller. This neither retries the stopped operation nor
  // changes its writer: the server projects the latest durable checkpoint.
  const rehydrateBoundMrChat = useCallback(async (): Promise<MrChatSessionView> => {
    const homeSessionId = activeSessionIdRef.current || activeSessionId;
    const navigationRevision = interactionRevisionRef.current;
    const mrSessionId = mrChatView?.session_id;
    if (!homeSessionId || homeSessionId === PENDING_ID || !mrSessionId) {
      throw new Error('Market Research session is unavailable.');
    }
    const view = await refreshBoundMrChat(homeSessionId);
    if (activeSessionIdRef.current === homeSessionId
      && interactionRevisionRef.current === navigationRevision
      && view.session_id === mrSessionId
      && (!view.home_chat_session_id || view.home_chat_session_id === homeSessionId)) {
      deliverResponseLlmResultEvents(view);
      applyMrChatView(view);
    }
    return view;
  }, [activeSessionId, applyMrChatView, deliverResponseLlmResultEvents, mrChatView?.session_id]);

  const submitSectionCHandoff = useCallback(async (): Promise<MrChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !mrChatView?.session_id) {
      throw new Error('Market Research session is unavailable.');
    }
    const homeSessionId = activeSessionId;
    const mrSessionId = mrChatView.session_id;
    const navigationRevision = interactionRevisionRef.current;
    const requestId = makeRequestId();
    setMrTurnInFlight({ homeSessionId, requestId });
    try {
      const view = await postMrChatSectionCHandoff(homeSessionId, requestId);
      if (activeSessionIdRef.current === homeSessionId
        && interactionRevisionRef.current === navigationRevision
        && view.session_id === mrSessionId
        && (!view.home_chat_session_id || view.home_chat_session_id === homeSessionId)) {
        deliverResponseLlmResultEvents(view);
        applyMrChatView(view);
      }
      return view;
    } finally {
      setMrTurnInFlight((current) => (
        current?.homeSessionId === homeSessionId && current.requestId === requestId
          ? null : current
      ));
    }
  }, [activeSessionId, activeSessionIdRef, applyMrChatView, deliverResponseLlmResultEvents, interactionRevisionRef, mrChatView?.session_id]);

  const notifyBoundMrQuestions = useCallback(async (requestId: string) => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !mrChatView?.session_id) {
      throw new Error('Market Research session is unavailable.');
    }
    return postMrChatQuestionsNotification(activeSessionId, requestId);
  }, [activeSessionId, mrChatView?.session_id]);

  const shareAndNotifyBoundMrQuestions = useCallback(async (emails: string[], requestId: string) => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !mrChatView?.session_id) {
      throw new Error('Market Research session is unavailable.');
    }
    return postMrChatQuestionsShareAndNotify(activeSessionId, emails, requestId);
  }, [activeSessionId, mrChatView?.session_id]);

  const uploadMrFiles = useCallback(async (files: File[]): Promise<string[]> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Market Research session is unavailable.');
    const flowId = mrChatView?.workflow?.flow_id;
    if (typeof flowId !== 'string' || !flowId) throw new Error('Market Research workflow is unavailable.');
    return uploadMrVendorQuotes(flowId, files);
  }, [activeSessionId, mrChatView?.workflow?.flow_id]);

  const getHandoffContext = useCallback((
    workflowHandoffReceipt?: string,
    originatingSessionId?: string,
  ): HomeChatHandoffContext | undefined => {
    const homeChatSessionId = originatingSessionId ?? activeSessionIdRef.current;
    return homeChatSessionId && homeChatSessionId !== PENDING_ID
      ? {
        homeChatSessionId,
        ...(workflowHandoffReceipt ? { workflowHandoffReceipt } : {}),
      }
      : undefined;
  }, []);

  const confirmWorkflowNavigation = useCallback((
    previous?: import('./types').WorkflowNavigationCheckpoint,
  ): import('./types').WorkflowNavigationCheckpoint | false => {
    if (displayMode !== 'companion') {
      return previous ?? HOME_NAVIGATION_CHECKPOINT;
    }
    if (!onBeforeWorkflowNavigation) {
      return previous ?? HOME_NAVIGATION_CHECKPOINT;
    }
    return onBeforeWorkflowNavigation(previous);
  }, [displayMode, onBeforeWorkflowNavigation]);

  const applyBoundConstructionSowResponse = useCallback((
    homeSessionId: string,
    view: ConstructionSowChatSessionView,
  ): ConstructionSowChatSessionView => {
    if (
      view.home_chat_session_id
      && view.home_chat_session_id !== homeSessionId
    ) {
      throw new Error('Construction SOW session is unavailable.');
    }
    if (activeSessionIdRef.current === homeSessionId) {
      applyConstructionSowChatView(view);
    }
    return view;
  }, [applyConstructionSowChatView]);

  const submitBoundConstructionSowTurn = useCallback(async (
    input: ConstructionSowChatTurnInput,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !constructionSowChatView?.session_id) {
      throw new Error('Construction SOW session is unavailable.');
    }
    const homeSessionId = activeSessionId;
    const view = await postConstructionSowChatTurn(homeSessionId, input);
    deliverResponseLlmResultEvents(view);
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [
    activeSessionId,
    applyBoundConstructionSowResponse,
    constructionSowChatView?.session_id,
    deliverResponseLlmResultEvents,
  ]);

  const uploadConstructionRequirement = useCallback(async (
    file: File,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await uploadConstructionSowRequirementDocument(
      homeSessionId,
      file,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const extractConstructionRequirement = useCallback(async (
    confirmOverwrite: boolean,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await extractConstructionSowRequirementDocument(
      homeSessionId,
      confirmOverwrite,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const removeConstructionRequirement = useCallback(async (): Promise<
    ConstructionSowChatSessionView
  > => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await removeConstructionSowRequirementDocument(
      homeSessionId,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const uploadConstructionSupport = useCallback(async (
    role: Exclude<ConstructionSowArtifactRole, 'requirements'>,
    file: File,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await uploadConstructionSowSupportDocument(
      homeSessionId,
      role,
      file,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const replaceConstructionSupport = useCallback(async (
    artifactId: string,
    file: File,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await replaceConstructionSowSupportDocument(
      homeSessionId,
      artifactId,
      file,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const removeConstructionSupport = useCallback(async (
    artifactId: string,
  ): Promise<ConstructionSowChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Construction SOW session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await removeConstructionSowSupportDocument(
      homeSessionId,
      artifactId,
      makeRequestId(),
    );
    return applyBoundConstructionSowResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundConstructionSowResponse]);

  const openGeneratedConstructionSow = useCallback(async (): Promise<void> => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !constructionSowChatView?.session_id) {
      throw new Error('This generated SOW is no longer available.');
    }
    if (!onOpenConstructionSowWorkflow) {
      throw new Error('This generated SOW is no longer available.');
    }
    const navigationCheckpoint = confirmWorkflowNavigation();
    if (navigationCheckpoint === false) return;
    const homeSessionId = activeSessionId;
    const target = await apiOpenGeneratedConstructionSow(
      homeSessionId,
      makeRequestId(),
    );
    if (activeSessionIdRef.current !== homeSessionId) return;
    if (confirmWorkflowNavigation(navigationCheckpoint) === false) return;
    onOpenConstructionSowWorkflow(
      target.flow_id,
      getHandoffContext(undefined, homeSessionId),
    );
  }, [
    activeSessionId,
    confirmWorkflowNavigation,
    constructionSowChatView?.session_id,
    getHandoffContext,
    onOpenConstructionSowWorkflow,
  ]);

  const applyBoundServicesPwsResponse = useCallback((homeSessionId: string, view: ServicesPwsChatSessionView): ServicesPwsChatSessionView => {
    if (view.home_chat_session_id && view.home_chat_session_id !== homeSessionId) throw new Error('Services PWS session is unavailable.');
    if (activeSessionIdRef.current === homeSessionId) applyServicesPwsChatView(view);
    return view;
  }, [applyServicesPwsChatView]);

  const submitBoundServicesPwsTurn = useCallback(async (input: ServicesPwsChatTurnInput): Promise<ServicesPwsChatSessionView> => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !servicesPwsChatView?.session_id) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    const view = await postServicesPwsChatTurn(homeSessionId, input);
    deliverResponseLlmResultEvents(view);
    return applyBoundServicesPwsResponse(homeSessionId, view);
  }, [activeSessionId, applyBoundServicesPwsResponse, deliverResponseLlmResultEvents, servicesPwsChatView?.session_id]);

  const uploadServicesRequirement = useCallback(async (file: File) => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await uploadServicesPwsRequirementDocument(homeSessionId, file, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const extractServicesRequirement = useCallback(async (confirmOverwrite: boolean) => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await extractServicesPwsRequirementDocument(homeSessionId, confirmOverwrite, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const removeServicesRequirement = useCallback(async () => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await removeServicesPwsRequirementDocument(homeSessionId, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const uploadServicesSupport = useCallback(async (role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File) => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await uploadServicesPwsSupportDocument(homeSessionId, role, file, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const replaceServicesSupport = useCallback(async (artifactId: string, role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File) => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await replaceServicesPwsSupportDocument(homeSessionId, artifactId, role, file, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const removeServicesSupport = useCallback(async (artifactId: string) => {
    if (!activeSessionId || activeSessionId === PENDING_ID) throw new Error('Services PWS session is unavailable.');
    const homeSessionId = activeSessionId;
    return applyBoundServicesPwsResponse(homeSessionId, await removeServicesPwsSupportDocument(homeSessionId, artifactId, makeRequestId()));
  }, [activeSessionId, applyBoundServicesPwsResponse]);
  const openGeneratedServicesPws = useCallback(async (): Promise<void> => {
    if (!activeSessionId || activeSessionId === PENDING_ID || !servicesPwsChatView?.session_id || !onOpenGeneratedServicesPwsWorkflow) throw new Error('This generated PWS is no longer available.');
    const checkpoint = confirmWorkflowNavigation(); if (checkpoint === false) return;
    const homeSessionId = activeSessionId;
    const target = await apiOpenGeneratedServicesPws(homeSessionId, makeRequestId());
    if (activeSessionIdRef.current !== homeSessionId || confirmWorkflowNavigation(checkpoint) === false) return;
    onOpenGeneratedServicesPwsWorkflow(target.flow_id, getHandoffContext(undefined, homeSessionId));
  }, [activeSessionId, confirmWorkflowNavigation, getHandoffContext, onOpenGeneratedServicesPwsWorkflow, servicesPwsChatView?.session_id]);

  /**
   * MR handoffs carry a named stage for the panel's own display/recovery
   * contract, whereas the application shell accepts typed GPC form keys only.
   * Re-enter the normal MR flow without relaying that opaque string: App
   * refetches owner-authorized MR metadata and restores its canonical current
   * stage. This is intentionally not a cast to a GPC stage key.
   */
  const openMrWorkflow = useCallback((flowId: string, _targetStageHint?: string) => {
    if (confirmWorkflowNavigation() === false) return;
    const context = getHandoffContext();
    if (onOpenMrWorkflow) onOpenMrWorkflow(flowId, context);
    else onOpenWorkflow?.(flowId, undefined, context);
  }, [confirmWorkflowNavigation, getHandoffContext, onOpenMrWorkflow, onOpenWorkflow]);

  const openGpcWorkflow = useCallback((
    flowId: string,
    targetStageHint?: GpcChatWorkflowStageKey,
  ) => {
    if (confirmWorkflowNavigation() === false) return;
    onOpenWorkflow?.(flowId, targetStageHint, getHandoffContext());
  }, [confirmWorkflowNavigation, getHandoffContext, onOpenWorkflow]);

  return {
    submitWorkflowOnlyChoice,
    submitNotificationBoundMrTurn,
    rehydrateBoundMrChat,
    submitSectionCHandoff,
    notifyBoundMrQuestions,
    shareAndNotifyBoundMrQuestions,

    submitGpcConfirmation,
    submitBoundGpcTurn,
    submitBoundMrTurn,
    uploadMrFiles,
    getHandoffContext,
    confirmWorkflowNavigation,
    submitBoundConstructionSowTurn,
    uploadConstructionRequirement,
    extractConstructionRequirement,
    removeConstructionRequirement,
    uploadConstructionSupport,
    replaceConstructionSupport,
    removeConstructionSupport,
    openGeneratedConstructionSow,
    submitBoundServicesPwsTurn,
    uploadServicesRequirement,
    extractServicesRequirement,
    removeServicesRequirement,
    uploadServicesSupport,
    replaceServicesSupport,
    removeServicesSupport,
    openGeneratedServicesPws,
    openMrWorkflow,
    openGpcWorkflow,
  };
}

export type HomeChatWorkflowHandoffs = ReturnType<typeof useHomeChatWorkflowHandoffs>;
